import { hypot } from '@gardener/core';
import { TILE_SIZE } from './config';
import { CrateKind } from './economy';
import { FURNITURE, FURNITURE_BY_CHAR } from './furniture';
import { BASE } from './minions';
import { ROYALE } from './royale';
import { SCARECROW } from './targets';
import { Team } from './teams';
import {
  indexShapes,
  placeShapes,
  type Shape,
  type ShapeIndex,
  Stop,
  segmentShape,
  type Thing,
  type ThingKind,
} from './things';
import { VISION } from './vision';

/** Ground layer of a cell. Stored per cell in `MapDef.ground`. */
export const Ground = {
  Grass: 0,
  Flowers: 1,
  Dirt: 2,
  Cobble: 3,
  Water: 4,
  Hedge: 5,
  /** Jungle floor (walkable). */
  Moss: 6,
  /** Jungle wall: dense undergrowth (like a hedge: blocks walking, shots and sight). */
  Thicket: 7,
  /** Burnt earth where a Railgun's tunnel went through a hedge or thicket (ADR-0038): open ground. */
  Scorched: 8,
  /** Withered grass (the range's abandoned garden, ADR-0101): open ground, drawn dry and dead. */
  Withered: 9,
  /** A house's wall (ADR-0120): blocks walking, shots and sight, and ends sight (no glimpse past it). */
  Wall: 10,
  /** A house's window (ADR-0120): stops feet, not shots; from inside you see out of it, from outside you see the roof. */
  Window: 11,
  /** A house's floor (ADR-0120): open ground under the roof (its doorways too). */
  Floor: 12,
  /**
   * The void round an arena's platform (ADR-0164: the Barrow's lava, far below): nothing to stand on, open to shots and
   * to sight.
   */
  Abyss: 13,
  /** An arena's platform (ADR-0164): open ground, drawn by the platform's own picture, not tile by tile. */
  Slab: 14,
} as const;
export type Ground = (typeof Ground)[keyof typeof Ground];

/** Objects standing on a cell (the apple crates are entities since ADR-0068: `MapDef.crates`). */
export const PropKind = { Tree: 1, Rock: 2, JungleTree: 3, DeadTree: 4 } as const;
export type PropKind = (typeof PropKind)[keyof typeof PropKind];

/**
 * Collision flags per cell. `Lava`: a pit of lava (Zombie Nights' hell, ADR-0187, `setLava`), `Solid` too: every foot
 * but a gardener's stays out of it, and a gardener who steps in falls in.
 */
export const CellFlag = { Solid: 1, BlocksShots: 2, Lava: 4 } as const;

export interface Prop {
  kind: PropKind;
  cx: number;
  cy: number;
}

export interface SpawnPoint {
  /** World position (cell centre, ground plane). */
  x: number;
  y: number;
}

export interface TeamPoint extends SpawnPoint {
  team: Team;
}

/** A rectangle of cells: top-left (cx, cy), `w` × `h`. */
export interface Block {
  cx: number;
  cy: number;
  w: number;
  h: number;
}

export interface MapDef {
  id: string;
  name: string;
  /**
   * A `zombies` map (ADR-0098) has one base (the Greenhouse) and the graveyard gates the night's zombies rise from;
   * `training` maps (the range's, the battle royale's) have neither.
   */
  mode: 'training' | 'zombies';
  width: number;
  height: number;
  ground: Uint8Array;
  flags: Uint8Array;
  props: Prop[];
  /** Neutral spawns (training maps). */
  playerSpawns: SpawnPoint[];
  /** Per-team spawns (Zombie Nights' maps: the party's round the Greenhouse), indexed by Team. */
  teamSpawns: Record<number, SpawnPoint[]>;
  bases: TeamPoint[];
  /** Mini-boss dens (the range's zoo). A run of adjacent `M` cells in a row is one den at its centre. */
  bossDens: SpawnPoint[];
  scarecrowSpawns: SpawnPoint[];
  /**
   * Crate spots (ADR-0068): each has a breakable crate standing on it that grows back (the sim's entities; the cell itself
   * is open ground) — an apple crate (`c`), a treasure chest full of gun cards (`$`, the shooting range's) or an
   * old pickup truck that blows up (`x`, the range's, ADR-0100).
   */
  crates: (SpawnPoint & { kind: CrateKind })[];
  /** Jungle creep camps (generated maps, ADR-0019): where each camp lives and which creeps (content CreepKind). */
  camps: (SpawnPoint & { kind: number })[];
  /**
   * The shooting range's sparring minions (`m`, ADR-0035; `w` a mega one, ADR-0100): each spot always has one standing
   * its ground.
   */
  sparring: (SpawnPoint & { mega: boolean })[];
  /** The shooting range's cottages (`H` blocks, ADR-0100): solid, drawn as one house over the block (cells). */
  houses: Block[];
  /** The shooting range's moving targets (`>`, ADR-0100): a dummy sliding left and right along a row of water. */
  sliders: SpawnPoint[];
  /** Bridges (`_`, ADR-0100): open ground drawn as planks over the water (cell indices `cy * width + cx`). */
  bridges: number[];
  /** Graveyard gates (`Z`, ADR-0098): where a zombies map's night rises from, in reading order. */
  gates: SpawnPoint[];
  /**
   * The night each gate first opens (ADR-0176, by `gates`' index; opened for good): a map whose lanes stay where they are.
   * Null: tonight's side turns round the farm (Moonlit Farm, `nightGates`).
   */
  gateNights: readonly number[] | null;
  /**
   * Where a run's lessons stand their sign (ADR-0176): a cell by a lane's chokepoint, the best first, and the gate whose
   * lane it covers (`signCell` takes the first free one of an open gate). None: by the Greenhouse toward tonight's side.
   */
  signs: readonly LessonSign[];
  /** Chomper's drive and its trench (ADR-0180, content `CHOMPER`): a zombies map's; null on the others. */
  chomper: ChomperPath | null;
  /** What a zombies map's dawns pay, as a multiple of `NIGHTS`' purse (content `dawnGold`): 1 but on Moonlit Farm. */
  dawnScale: number;
  /** The range's ruined cottages (`h` blocks, ADR-0101): solid, drawn as one ruin over the block (cells). */
  ruins: Block[];
  /** Dead crops (`"`, ADR-0101): tilled earth with a withered crop on it, open ground (cell indices `cy * width + cx`). */
  crops: number[];
  /** The range's graves (`z`, ADR-0101): each always has a zombie of its `kind` (content ZombieKind) about it. */
  graves: (SpawnPoint & { kind: number })[];
  /** The range's slot machines (`j`, ADR-0103): solid; a right click on one spins its reels for a gun from the armoury. */
  slots: SpawnPoint[];
  /**
   * Seedbeds (`e`, a battle royale's, feedback #27): raised beds where a squad plants its fallen's seeds to grow them back
   * (`ROYALE.seed`). A run of `e` cells in a row is one bed at its middle; its cells stop feet, not shots or sight.
   */
  seedbeds: SpawnPoint[];
  /**
   * TOP SHOTS boards (`L`, a battle royale's lobby island's, ADR-0190 amended): a chalk slate on an easel ranking the
   * warm-up's best shots. A run of `L` cells in a row is one board, its foot at their middle; its cells stop feet, not
   * shots or sight.
   */
  boards: SpawnPoint[];
  /**
   * A battle royale's stone altars (ADR-0189, the map maker's `altars`): stand at one long enough and it wakes, its
   * guardian rises, and whoever brings it down wins an Epic gun (ADR-0215, `ROYALE.altar`). None on most maps.
   */
  altars: SpawnPoint[];
  /**
   * A battle royale's wanted boards (ADR-0229, the map maker's `posters`): each one's foot (px). A round lights a few
   * with a fresh poster (`ROYALE.wanted`); SPACE at one takes its bounty.
   */
  posters: SpawnPoint[];
  /**
   * A battle royale's roulette tables (ADR-0232, the map maker's `roulettes`): each one's foot (px). SPACE at one spins it
   * (`ROYALE.roulette`).
   */
  roulettes: SpawnPoint[];
  /**
   * The cars that stand here every round (ADR-0232 amended, the map maker's `parked`, a battle royale's: `parkCars`), at
   * the crooked headings their drivers left them at; the round's other cars spread out round them.
   */
  parked: { x: number; y: number; heading: number }[];
  /**
   * A battle royale's crypts (ADR-0202, five since ADR-0215: `MapSource.crypts`): sealed rooms of its underground, gone
   * into only by a squad holding the crypt key, any of them by any key. A keyed gardener walked into one's doorway from
   * the hall outside (`door`: the doorway's middle at its back wall's foot) comes out inside at `in`; walked into its way
   * out inside (`exit`: at the foot of the room's south wall, the doorway's middle), out at `out` in the hall. Its room:
   * world px `x0`…`x1` × `y0`…`y1`. None on most maps.
   */
  crypts: readonly Crypt[];
  /**
   * Stairs between the map proper and its underground (ADR-0196, `MapSource.stairs`): (x, y) where you come out of each up
   * top, before its stairwell (its steps go down southwards from there), (ux, uy) where you come out below, before the
   * foot of its steps (they go up northwards). Walked, never a key (`ROYALE.stairs`). None on most maps.
   */
  stairs: readonly { x: number; y: number; ux: number; uy: number }[];
  /**
   * A battle royale's ways through the air (ADR-0198, `AIR`): mushrooms to bounce off (each cap's middle), washing lines to
   * ride (where you get on and off at each end), ladders up a tower to glide off (where you stand to climb, and how high
   * its top is, px), sunflowers to be flung by (the leaf you stand on). None on most maps.
   */
  mushrooms: SpawnPoint[];
  washlines: readonly { ax: number; ay: number; bx: number; by: number }[];
  ladders: readonly { x: number; y: number; h: number }[];
  sunflowers: SpawnPoint[];
  /**
   * The underground's rails (ADR-0211, `AIR.cart`): straight, from where one gets in a mine cart at one end (ax, ay) to
   * the other's (bx, by), world px. None on most maps.
   */
  rails: readonly { ax: number; ay: number; bx: number; by: number }[];
  /**
   * A battle royale's fishing rods (ADR-0229, `MapSource.rods`): each round one lies on each spot (world px), there to
   * take. None on most maps.
   */
  rods: SpawnPoint[];
  /**
   * Its fishing waters (ADR-0229, `MapSource.fishing`): world px boxes [x0, y0, x1, y1] whose open water a float may be
   * cast onto (content `fishable`). None on most maps.
   */
  fishing: readonly (readonly [x0: number, y0: number, x1: number, y1: number])[];
  /** Its named places (`Place`: the map maker's), none on most maps. */
  places: readonly Place[];
  /**
   * Weapon altars (`!`, a battle royale's lobby island's, ADR-0190 amended): stone plinths one walks round (their cells
   * stop feet, not shots), each holding one of the chests' guns for anyone to take while the lobby lasts, the gun staying
   * on it for the next one (`ROYALE_LOOT.guns` in turn, west to east: Common first, ADR-0216).
   */
  gunAltars: SpawnPoint[];
  /**
   * The house each cell belongs to (ADR-0120: walls, windows, doorways and floor, 1 … `roofs.length`), 0 outside. From
   * outside a house you see its roof; what is inside is seen only from inside (`traceSight`, `canSee`).
   */
  interior: Uint8Array;
  /** Each house's rectangle (cells), house `i + 1`: its walls round its floor, the roof over all of it. */
  roofs: Block[];
  /** The houses' doorways (`D`, cell indices `cy * width + cx`): floor in a wall's line. */
  doors: number[];
  /**
   * What stands on each cell (ADR-0120 amended): 1 + its kind in content `FURNITURE`, 0 for nothing. A run of one kind is
   * one piece; a tall one stops shots and sight, a low one feet only (the cell's flags).
   */
  furniture: Uint8Array;
  /** 1 per cell under fog of war (the jungle), 0 on open ground everyone can see. */
  fog: Uint8Array;
  /** How far a gardener's sight cone reaches here (px): `VISION.coneRange`, a battle royale's maps further (`ROYALE.cone`). */
  cone: number;
  /**
   * How much bigger it is than the battle royale's distances were tuned on (ADR-0207: a map 128 cells across): the Frost's
   * circles and how long each takes (`ROYALE.zone`: its pace stays), and a hot-air balloon's pace (`BALLOON.speed`), by as
   * much.
   */
  scale: number;
  /**
   * Cells burnt out of this copy so far (`scorchCells`), and turned to lava or back (`setLava`): what caches what its
   * walls hide checks.
   */
  carved?: number;
  /** Things broken on this copy so far (`breakThing`, ADR-0188): what caches what stops sight check, with `carved`. */
  broke?: number;
  /**
   * The arenas under the map's own rows, each cut off from the map and from the others, top to bottom (`Arena`): the
   * boss arenas (ADR-0164, ADR-0165: Zombie Nights' Barrow and Fallow) and a battle royale's lobby island (ADR-0190
   * amended: `lobbyOf`). Nobody walks between them: the sim moves them.
   */
  arenas: readonly Arena[];
  /**
   * The tileset the client draws it in: the game's own, or Cainos' Village packs (`village`: the battle royale's Village,
   * ADR-0188, and Zombie Nights' farms, ADR-0206). Presentation only.
   */
  style?: MapStyle;
  /**
   * Things standing on it finer than its cells (ADR-0188): their kinds, the things (a kind at a foot), every thing's
   * shapes and the shapes by cell (`forShapes`). None on most maps.
   */
  kinds: readonly ThingKind[];
  things: readonly Thing[];
  shapes: readonly Shape[];
  shapeIndex: ShapeIndex;
  /** 1 for a thing shots broke this round (`breakThing`): its shapes stop nothing. Each world's or client's own copy. */
  broken: Uint8Array;
  /**
   * The house (ADR-0120) each thing is the walls of (a `ThingKind.house` standing on its block's south edge), 0 for any
   * other: sight that meets its shapes meets that house.
   */
  thingHouse: Uint8Array;
}

/** A map's look (`MapDef.style`): none is the game's own. */
export type MapStyle = 'village';

/**
 * A boss arena (ADR-0164): its first row `top` (the map proper is the rows above the first arena's: `yardOf`) and how
 * many `rows` it takes, `floor` the open ground there (a platform over an abyss) and `stage` what a camera in it shows
 * (the floor and what is round it, its source's `stage`).
 */
export interface Arena {
  top: number;
  rows: number;
  floor: Block;
  stage: Block;
  /**
   * A battle royale's lobby island (ADR-0190 amended), no boss's platform: where its gardeners warm up before the round,
   * on the map's own ground in its water, seen at the view's own zoom. The round's start moves them onto the map.
   */
  lobby?: true;
  /**
   * The underground (ADR-0196): tunnels in rock right under the map proper, its rows the map proper's cell for cell
   * (`surfaceOf`), walked down to and up from by the map's `stairs`.
   */
  under?: true;
}

/**
 * An arena's rows (of the map's width; one floor in them: a boss arena's `-` slab in `^` abyss, the lobby island's the
 * map's own ground in its water) and how many cells round its floor its `stage` shows: the abyss either `side`, `north`
 * of it what looms over its edge, `south` the platform's cliff (the lobby's: its water).
 */
export interface ArenaSource {
  rows: string;
  stage: { side: number; north: number; south: number };
  /** The lobby island (`Arena.lobby`). */
  lobby?: true;
  /** The underground (`Arena.under`): as many rows as the map proper's. */
  under?: true;
}

/** A cell a lesson's sign stands on (ADR-0176), by gate `gate`'s lane. */
export interface LessonSign {
  cx: number;
  cy: number;
  gate: number;
}

/**
 * Chomper's drive on a zombies map (ADR-0180, ADR-0186): its way, the cells it turns at (from off the map in, careening
 * round the whole yard and through the plaza as if it had lost its mind, out again the way it came: each leg straight
 * along a row or a column, two passes of its scream long). All it digs along it is its trench, one deep hole the dead
 * rise from (content `trenchOf`).
 */
export interface ChomperPath {
  route: readonly (readonly [number, number])[];
}

export interface MapSource {
  id: string;
  name: string;
  /** The tileset the client draws it in (`MapDef.style`). */
  style?: MapStyle;
  /**
   * One character per cell:
   * `#` hedge · `.` grass · `,` flowers · `:` dirt · `=` cobble · `~` water
   * `c` apple crate (breakable, ADR-0068) · `$` treasure chest that grows back (shooting range) · `&` a battle royale's mini
   * chest (ADR-0189) · `T` tree · `P` player spawn · `S` scarecrow
   * `B`/`R` blue/red base (a zombies map's Greenhouse) · `b`/`r` their player spawns · `M` mini-boss den (the range's zoo)
   * jungle: `;` moss · `%` thicket · `o` rock · `Y` jungle tree
   * the shooting range: `m` sparring minion · `w` a mega one · `H` cottage (a rectangle of them) · `>` moving target on
   * water · `_` bridge over water · `x` pickup truck that blows up · the abandoned garden (ADR-0101): `'` withered
   * grass · `h` ruined cottage · `"` dead crop · `t` dead tree · `z` grave · `j` slot machine (ADR-0103)
   * zombies: `Z` graveyard gate (ADR-0098) · an arena's (ADR-0164): `^` abyss · `-` its platform's slab
   * a battle royale's lobby island (ADR-0190 amended): `!` weapon altar
   * houses (ADR-0120): `W` wall · `+` window · `F` floor · `D` doorway — each closed rectangle of them is a house;
   * what stands in them (ADR-0120 amended, content `FURNITURE`): `n` table · `d` workbench · `l` bookshelf · `v` hay ·
   * `a` sofa · `u` bed · `k` stove · `q` barrel · `i` crates · `s` sacks · `p` potted plant
   * (objects/spawns stand on the most built ground around them: floor > cobble > dirt > moss > flowers > grass)
   */
  rows: string;
  /**
   * Arenas (ADR-0164; a battle royale's lobby island, ADR-0190 amended): rows of the map's width that go under `rows`,
   * one after the other (`MapDef.arenas`). Whatever is on them is the arena's (crates, scarecrows), never a base, a spawn
   * or a gate.
   */
  arenas?: readonly ArenaSource[];
  /** A zombies map's gates' opening nights and its lessons' signs (ADR-0176: `MapDef.gateNights`, `MapDef.signs`). */
  gateNights?: readonly number[];
  signs?: readonly LessonSign[];
  /** A zombies map's Chomper (ADR-0180, `MapDef.chomper`). */
  chomper?: ChomperPath;
  /** A zombies map's dawn purse (`MapDef.dawnScale`, 1 by default). */
  dawnScale?: number;
  /** The whole map lies under fog of war, like the jungle (the battle royale's maps). */
  fogged?: boolean;
  /** How far a gardener's sight cone reaches (px) when not `VISION.coneRange` (a battle royale's maps: `ROYALE.cone`). */
  cone?: number;
  /** A battle royale's map's size against 128 cells across (`MapDef.scale`, default 1). */
  scale?: number;
  /**
   * The jungle lies under fog of war: everything but the open ground the bases stand in — what a walk from a base (from
   * the spawns on a map without bases) reaches
   * without setting foot on moss or thicket (the plazas, lanes and their hedges). A hand-made map with a jungle of its
   * own, not rows round a generated one (the shooting range).
   */
  fogJungle?: boolean;
  /** The creature (content CreepKind) of each creep camp (`K`), in reading order; 0 (snails) past its end. */
  campKinds?: readonly number[];
  /** The zombie (content ZombieKind) of each grave (`z`), in reading order; 0 (shamblers) past its end. */
  graveKinds?: readonly number[];
  /** Things standing on it finer than its cells (ADR-0188): their kinds, and each thing as [kind, foot x, foot y] (px). */
  kinds?: readonly ThingKind[];
  things?: readonly (readonly [kind: number, x: number, y: number, lift?: number])[];
  /** A battle royale's stone altars (ADR-0189, `MapDef.altars`): each one's middle (px). */
  altars?: readonly (readonly [x: number, y: number])[];
  /** A battle royale's wanted boards (ADR-0229, `MapDef.posters`): each one's foot (px). */
  posters?: readonly (readonly [x: number, y: number])[];
  /** A battle royale's roulette tables (ADR-0232, `MapDef.roulettes`): each one's foot (px). */
  roulettes?: readonly (readonly [x: number, y: number])[];
  /** The cars that stand here every round (`MapDef.parked`): each one's middle (px) and heading (rad). */
  parked?: readonly (readonly [x: number, y: number, heading: number])[];
  /**
   * A battle royale's crypts (ADR-0202, ADR-0215: `MapDef.crypts`), world px: each one's doorway from the hall, where one
   * comes out inside, its way out inside, where one comes out in the hall, and its room's box.
   */
  crypts?: readonly (readonly [
    doorX: number,
    doorY: number,
    inX: number,
    inY: number,
    exitX: number,
    exitY: number,
    outX: number,
    outY: number,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
  ])[];
  /** Its crypts' loot (ADR-0202, `Crypt.loot`): each one's crypt (its index in `crypts`), content PickupKind and spot (px). */
  cryptLoot?: readonly (readonly [crypt: number, kind: number, x: number, y: number])[];
  /**
   * Stairs between the map proper and the underground (ADR-0196), world px: where you come out of each up top (north of
   * its stairwell), and below (south of its steps' foot); walking down the stairwell, or up the steps, takes you to the
   * other.
   */
  stairs?: readonly (readonly [x: number, y: number, ux: number, uy: number])[];
  /**
   * Its ways through the air (ADR-0198, `MapDef.mushrooms` …), world px: a mushroom's cap, a sunflower's leaf [x, y]; a
   * washing line's ends [ax, ay, bx, by]; a ladder's foot and its top's height [x, y, h].
   */
  mushrooms?: readonly (readonly [x: number, y: number])[];
  washlines?: readonly (readonly [ax: number, ay: number, bx: number, by: number])[];
  ladders?: readonly (readonly [x: number, y: number, h: number])[];
  sunflowers?: readonly (readonly [x: number, y: number])[];
  /** The underground's rails (ADR-0211, `MapDef.rails`), world px: each one's ends [ax, ay, bx, by]. */
  rails?: readonly (readonly [ax: number, ay: number, bx: number, by: number])[];
  /** Its fishing rods' spots and its fishing waters (ADR-0229, `MapDef.rods`, `MapDef.fishing`), world px. */
  rods?: readonly (readonly [x: number, y: number])[];
  fishing?: readonly (readonly [x0: number, y0: number, x1: number, y1: number])[];
  /** Named places (`MapDef.places`). */
  places?: readonly Place[];
}

/**
 * A named place on a map (a battle royale's: the market square, the watchtower): where its name is written on the map
 * (px). A `major` one is named on a small map too; every one on a big one.
 */
export interface Place {
  name: string;
  x: number;
  y: number;
  major?: true;
}

/** The base (and, lowercase, spawn) character of each team. */
const BASE_TEAM: Record<string, Team> = { B: Team.Blue, R: Team.Red };

const GROUND_CHARS: Record<string, Ground> = {
  '#': Ground.Hedge,
  '.': Ground.Grass,
  ',': Ground.Flowers,
  ':': Ground.Dirt,
  '=': Ground.Cobble,
  '~': Ground.Water,
  ';': Ground.Moss,
  '%': Ground.Thicket,
  "'": Ground.Withered,
  W: Ground.Wall,
  '+': Ground.Window,
  F: Ground.Floor,
  D: Ground.Floor,
  // A house's rim whose walls are things (ADR-0188): floor, its wall a thin shape along the house's edge.
  '*': Ground.Floor,
  '^': Ground.Abyss,
  '-': Ground.Slab,
};

const flagsFor = (g: Ground): number =>
  g === Ground.Hedge || g === Ground.Thicket || g === Ground.Wall
    ? CellFlag.Solid | CellFlag.BlocksShots
    : g === Ground.Water || g === Ground.Window || g === Ground.Abyss
      ? CellFlag.Solid
      : 0;

/** A house's own cells (ADR-0120): what makes up its rectangle. */
const HOUSE_CHARS = new Set(['W', '+', 'F', 'D', '*']);

export function parseMap(src: MapSource): MapDef {
  const rowsOf = (rows: string) =>
    rows
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
  const main = rowsOf(src.rows);
  const bands = (src.arenas ?? []).map((a) => rowsOf(a.rows));
  const lines = [...main, ...bands.flat()];
  const height = lines.length;
  const width = lines[0]?.length ?? 0;
  if (width === 0) throw new Error(`Map ${src.id}: empty`);

  const ground = new Uint8Array(width * height);
  const flags = new Uint8Array(width * height);
  const props: Prop[] = [];
  const playerSpawns: SpawnPoint[] = [];
  const scarecrowSpawns: SpawnPoint[] = [];
  const teamSpawns: Record<number, SpawnPoint[]> = { [Team.Blue]: [], [Team.Red]: [] };
  const bases: TeamPoint[] = [];
  const bossDens: SpawnPoint[] = [];
  const crates: MapDef['crates'] = [];
  const camps: MapDef['camps'] = [];
  const sparring: MapDef['sparring'] = [];
  const sliders: SpawnPoint[] = [];
  const houses: Block[] = [];
  const bridges: number[] = [];
  const gates: SpawnPoint[] = [];
  const ruins: Block[] = [];
  const crops: number[] = [];
  const graves: MapDef['graves'] = [];
  const slots: SpawnPoint[] = [];
  const seedbeds: SpawnPoint[] = [];
  const boards: SpawnPoint[] = [];
  const gunAltars: SpawnPoint[] = [];
  const doors: number[] = [];
  const furniture = new Uint8Array(width * height);
  const centre = (c: number) => (c + 0.5) * TILE_SIZE;

  lines.forEach((line, cy) => {
    if (line.length !== width) {
      throw new Error(`Map ${src.id}: row ${cy} has ${line.length} cells, expected ${width}`);
    }
  });
  const charAt = (cx: number, cy: number) => lines[cy]?.[cx] ?? '#';

  // Pass 1: ground cells.
  for (let cy = 0; cy < height; cy++) {
    for (let cx = 0; cx < width; cx++) {
      const g = GROUND_CHARS[charAt(cx, cy)];
      if (g === undefined) continue;
      ground[cy * width + cx] = g;
      flags[cy * width + cx] = flagsFor(g);
      if (charAt(cx, cy) === 'D') doors.push(cy * width + cx);
    }
  }

  // Pass 2: objects and spawns stand on the "most built" ground among their 4 neighbours
  // (cobble > dirt > moss > flowers > grass). Order-independent, so mirrored maps render mirrored.
  const GROUND_PRIORITY: Ground[] = [
    Ground.Floor,
    Ground.Slab,
    Ground.Cobble,
    Ground.Dirt,
    Ground.Moss,
    Ground.Flowers,
    Ground.Grass,
    Ground.Withered,
  ];
  for (let cy = 0; cy < height; cy++) {
    for (let cx = 0; cx < width; cx++) {
      const ch = charAt(cx, cy);
      if (GROUND_CHARS[ch] !== undefined) continue;
      const i = cy * width + cx;
      const around = [charAt(cx - 1, cy), charAt(cx + 1, cy), charAt(cx, cy - 1), charAt(cx, cy + 1)]
        .map((c) => GROUND_CHARS[c])
        .filter((g): g is Ground => g !== undefined);
      ground[i] = GROUND_PRIORITY.find((g) => around.includes(g)) ?? Ground.Grass;
      const thing = FURNITURE_BY_CHAR[ch];
      if (thing !== undefined) {
        furniture[i] = thing + 1;
        flags[i] = CellFlag.Solid | (FURNITURE[thing]?.tall ? CellFlag.BlocksShots : 0);
        continue;
      }
      switch (ch) {
        case 'c':
          crates.push({ x: centre(cx), y: centre(cy), kind: CrateKind.Apple });
          break;
        case '!':
          // A weapon altar (ADR-0190 amended): a stone plinth one walks round, its gun floating over it.
          flags[i] = CellFlag.Solid;
          gunAltars.push({ x: centre(cx), y: centre(cy) });
          break;
        case '$':
          crates.push({ x: centre(cx), y: centre(cy), kind: CrateKind.Airdrop });
          break;
        case '&':
          crates.push({ x: centre(cx), y: centre(cy), kind: CrateKind.Mini });
          break;
        case 'x':
          crates.push({ x: centre(cx), y: centre(cy), kind: CrateKind.Truck });
          break;
        case '_':
          ground[i] = Ground.Dirt;
          bridges.push(i);
          break;
        case 'T':
          props.push({ kind: PropKind.Tree, cx, cy });
          flags[i] = CellFlag.Solid | CellFlag.BlocksShots;
          break;
        case 'o':
          props.push({ kind: PropKind.Rock, cx, cy });
          flags[i] = CellFlag.Solid | CellFlag.BlocksShots;
          break;
        case 'Y':
          props.push({ kind: PropKind.JungleTree, cx, cy });
          flags[i] = CellFlag.Solid | CellFlag.BlocksShots;
          break;
        case 't':
          props.push({ kind: PropKind.DeadTree, cx, cy });
          flags[i] = CellFlag.Solid | CellFlag.BlocksShots;
          break;
        case '"':
          ground[i] = Ground.Dirt;
          crops.push(i);
          break;
        case 'z':
          graves.push({ x: centre(cx), y: centre(cy), kind: src.graveKinds?.[graves.length] ?? 0 });
          break;
        case 'j':
          flags[i] = CellFlag.Solid | CellFlag.BlocksShots;
          slots.push({ x: centre(cx), y: centre(cy) });
          break;
        case 'e':
          ground[i] = Ground.Dirt;
          flags[i] = CellFlag.Solid;
          if (charAt(cx - 1, cy) === 'e') break;
          {
            let end = cx;
            while (charAt(end + 1, cy) === 'e') end++;
            seedbeds.push({ x: ((cx + end + 1) / 2) * TILE_SIZE, y: centre(cy) });
          }
          break;
        case 'L':
          flags[i] = CellFlag.Solid;
          if (charAt(cx - 1, cy) === 'L') break;
          {
            let end = cx;
            while (charAt(end + 1, cy) === 'L') end++;
            boards.push({ x: ((cx + end + 1) / 2) * TILE_SIZE, y: (cy + 1) * TILE_SIZE });
          }
          break;
        case 'K':
          // A creep camp's centre; its kind is the map's `campKinds` (ADR-0019).
          camps.push({ x: centre(cx), y: centre(cy), kind: src.campKinds?.[camps.length] ?? 0 });
          break;
        case 'P':
          playerSpawns.push({ x: centre(cx), y: centre(cy) });
          break;
        case 'S':
          scarecrowSpawns.push({ x: centre(cx), y: centre(cy) });
          break;
        case 'B':
        case 'R': {
          // A 2 × 2 block of them is one base on the corner between its cells (Zombie Nights' Greenhouse where its lanes,
          // two cells wide, cross: ADR-0206 amended).
          if (charAt(cx - 1, cy) === ch || charAt(cx, cy - 1) === ch) break;
          const [w, h] = [charAt(cx + 1, cy) === ch ? 2 : 1, charAt(cx, cy + 1) === ch ? 2 : 1];
          bases.push({ x: (cx + w / 2) * TILE_SIZE, y: (cy + h / 2) * TILE_SIZE, team: BASE_TEAM[ch] as Team });
          break;
        }
        case 'b':
        case 'r':
          (teamSpawns[BASE_TEAM[ch.toUpperCase()] as Team] as SpawnPoint[]).push({ x: centre(cx), y: centre(cy) });
          break;
        case 'M':
          // A run of Ms (e.g. the two cells straddling a mirrored map's centre line) is one den.
          if (charAt(cx - 1, cy) === 'M') break;
          {
            let end = cx;
            while (charAt(end + 1, cy) === 'M') end++;
            bossDens.push({ x: ((cx + end + 1) / 2) * TILE_SIZE, y: centre(cy) });
          }
          break;
        case 'm':
        case 'w':
          sparring.push({ x: centre(cx), y: centre(cy), mega: ch === 'w' });
          break;
        case 'Z':
          gates.push({ x: centre(cx), y: centre(cy) });
          break;
        case 'H':
        case 'h':
          flags[i] = CellFlag.Solid | CellFlag.BlocksShots;
          if (charAt(cx - 1, cy) === ch || charAt(cx, cy - 1) === ch) break;
          {
            let w = 1;
            let h = 1;
            while (charAt(cx + w, cy) === ch) w++;
            while (charAt(cx, cy + h) === ch) h++;
            (ch === 'H' ? houses : ruins).push({ cx, cy, w, h });
          }
          break;
        case '>':
          ground[i] = Ground.Water;
          flags[i] = CellFlag.Solid;
          sliders.push({ x: centre(cx), y: centre(cy) });
          break;
        default:
          throw new Error(`Map ${src.id}: unknown cell '${ch}' at ${cx},${cy}`);
      }
    }
  }

  const { interior, roofs } = housesOf(src.id, width, height, charAt);

  const mode = gates.length > 0 ? 'zombies' : 'training';
  if (mode === 'zombies') {
    // One garden against the night (ADR-0098): its base, its spawns round it, the gates.
    if (bases.length !== 1) throw new Error(`Map ${src.id}: a zombies map needs exactly one base`);
    const team = (bases[0] as TeamPoint).team;
    if ((teamSpawns[team] ?? []).length === 0) throw new Error(`Map ${src.id}: missing spawns for team ${team}`);
  } else if (bases.length > 0) {
    throw new Error(`Map ${src.id}: a base without graveyard gates (only a zombies map has one)`);
  } else if (playerSpawns.length === 0 && (teamSpawns[Team.Blue]?.length === 0 || teamSpawns[Team.Red]?.length === 0)) {
    throw new Error(`Map ${src.id}: needs at least one P, or b and r spawns`);
  }
  const fog = new Uint8Array(width * height);
  if (src.fogged) fog.fill(1);
  if (src.fogJungle) {
    fog.fill(1);
    const wild = (i: number) => (ground[i] as Ground) === Ground.Moss || (ground[i] as Ground) === Ground.Thicket;
    // A map without bases (the range, ADR-0100) is seen from its spawns.
    const queue = (bases.length > 0 ? bases : playerSpawns).map(
      (b) => Math.floor(b.y / TILE_SIZE) * width + Math.floor(b.x / TILE_SIZE),
    );
    for (const i of queue) fog[i] = 0;
    for (let k = 0; k < queue.length; k++) {
      const i = queue[k] as number;
      const x = i % width;
      for (const j of [x > 0 ? i - 1 : -1, x < width - 1 ? i + 1 : -1, i - width, i + width]) {
        if (j < 0 || j >= width * height || fog[j] === 0 || wild(j)) continue;
        fog[j] = 0;
        // A hedge is seen from the lane it walls; the walk goes no further through it.
        if ((ground[j] as Ground) !== Ground.Hedge) queue.push(j);
      }
    }
  }
  // A house is never open ground (ADR-0120): what is inside it is seen only from inside, on any map.
  for (let i = 0; i < interior.length; i++) if (interior[i]) fog[i] = 1;
  return {
    id: src.id,
    name: src.name,
    mode,
    width,
    height,
    ground,
    flags,
    props,
    playerSpawns,
    teamSpawns,
    bases,
    bossDens,
    scarecrowSpawns,
    crates,
    camps,
    sparring,
    houses,
    sliders,
    bridges,
    gates,
    gateNights: src.gateNights ?? null,
    signs: src.signs ?? [],
    chomper: src.chomper ?? null,
    dawnScale: src.dawnScale ?? 1,
    ruins,
    crops,
    graves,
    slots,
    seedbeds,
    boards,
    altars: (src.altars ?? []).map(([x, y]) => ({ x, y })),
    posters: (src.posters ?? []).map(([x, y]) => ({ x, y })),
    roulettes: (src.roulettes ?? []).map(([x, y]) => ({ x, y })),
    parked: (src.parked ?? []).map(([x, y, heading]) => ({ x, y, heading })),
    crypts: (src.crypts ?? []).map((c, i) => ({
      door: { x: c[0], y: c[1] },
      in: { x: c[2], y: c[3] },
      exit: { x: c[4], y: c[5] },
      out: { x: c[6], y: c[7] },
      x0: c[8],
      y0: c[9],
      x1: c[10],
      y1: c[11],
      loot: (src.cryptLoot ?? []).filter(([k]) => k === i).map(([, kind, x, y]) => ({ kind, x, y })),
    })),
    stairs: (src.stairs ?? []).map(([x, y, ux, uy]) => ({ x, y, ux, uy })),
    mushrooms: (src.mushrooms ?? []).map(([x, y]) => ({ x, y })),
    washlines: (src.washlines ?? []).map(([ax, ay, bx, by]) => ({ ax, ay, bx, by })),
    ladders: (src.ladders ?? []).map(([x, y, h]) => ({ x, y, h })),
    sunflowers: (src.sunflowers ?? []).map(([x, y]) => ({ x, y })),
    rails: (src.rails ?? []).map(([ax, ay, bx, by]) => ({ ax, ay, bx, by })),
    rods: (src.rods ?? []).map(([x, y]) => ({ x, y })),
    fishing: src.fishing ?? [],
    places: src.places ?? [],
    gunAltars,
    interior,
    roofs,
    doors,
    furniture,
    fog,
    arenas: (src.arenas ?? []).map((a, i) => {
      const arena = arenaOf(
        src.id,
        width,
        main.length + bands.slice(0, i).reduce((n, b) => n + b.length, 0),
        (bands[i] as string[]).length,
        a.stage,
        flags,
      );
      return a.lobby ? { ...arena, lobby: true } : a.under ? { ...arena, under: true } : arena;
    }),
    cone: src.cone ?? VISION.coneRange,
    scale: src.scale ?? 1,
    ...(src.style ? { style: src.style } : {}),
    ...thingsOf(src, width, height, interior),
  };
}

/** A map source's things (ADR-0188): placed, their shapes indexed by cell, none broken. */
function thingsOf(src: MapSource, width: number, height: number, interior: Uint8Array) {
  const kinds = src.kinds ?? [];
  const things: Thing[] = (src.things ?? []).map(([kind, x, y, lift]) => {
    if (!kinds[kind]) throw new Error(`Map ${src.id}: a thing of unknown kind ${kind}`);
    return lift ? { kind, x, y, lift } : { kind, x, y };
  });
  const shapes = things.flatMap((t, i) => placeShapes(kinds[t.kind] as ThingKind, t, i));
  // A house's walls stand a hair inside its cells: the house under the first of them that is in one.
  const at = (x: number, y: number) => {
    const [cx, cy] = [Math.floor(x / TILE_SIZE), Math.floor(y / TILE_SIZE)];
    return cx >= 0 && cy >= 0 && cx < width && cy < height ? (interior[cy * width + cx] as number) : 0;
  };
  const houseOf = (t: Thing, i: number) =>
    shapes.reduce((id, s) => id || (s.thing === i ? at(s.x, s.y) : 0), 0) || at(t.x, t.y - 4);
  return {
    kinds,
    things,
    shapes,
    shapeIndex: indexShapes(shapes, width, height),
    broken: new Uint8Array(things.length),
    thingHouse: Uint8Array.from(things, (t, i) => (kinds[t.kind]?.house ? houseOf(t, i) : 0)),
  };
}

/** The arena in the `rows` rows from `top` down (ADR-0164): its floor is every open cell there, its stage `m` round it. */
function arenaOf(
  id: string,
  width: number,
  top: number,
  rows: number,
  m: ArenaSource['stage'],
  flags: Uint8Array,
): Arena {
  const bottom = top + rows;
  let [x0, y0, x1, y1] = [width, bottom, -1, -1];
  for (let cy = top; cy < bottom; cy++)
    for (let cx = 0; cx < width; cx++) {
      if (((flags[cy * width + cx] as number) & CellFlag.Solid) !== 0) continue;
      [x0, y0, x1, y1] = [Math.min(x0, cx), Math.min(y0, cy), Math.max(x1, cx), Math.max(y1, cy)];
    }
  if (x1 < 0) throw new Error(`Map ${id}: its arena has no floor`);
  if (x0 < m.side || x1 >= width - m.side || y0 < top + m.north || y1 >= bottom - m.south)
    throw new Error(`Map ${id}: its arena's floor is too near the edge for its stage`);
  return {
    top,
    rows,
    floor: { cx: x0, cy: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 },
    stage: { cx: x0 - m.side, cy: y0 - m.north, w: x1 - x0 + 1 + m.side * 2, h: y1 - y0 + 1 + m.north + m.south },
  };
}

/** The rows of the map proper: all of them, or those above its first arena (ADR-0164). */
export const yardRows = (map: Pick<MapDef, 'height' | 'arenas'>): number => map.arenas[0]?.top ?? map.height;

/** The arena world point (x, y) is in (ADR-0164), or null: on the map proper. */
export const arenaAt = (map: Pick<MapDef, 'arenas'>, y: number): Arena | null =>
  map.arenas.findLast((a) => y >= a.top * TILE_SIZE) ?? null;

/** Is world point (x, y) in one of the map's arenas (ADR-0164)? */
export const inArena = (map: Pick<MapDef, 'arenas'>, y: number): boolean => arenaAt(map, y) !== null;

/** A battle royale's lobby island (ADR-0190 amended: `Arena.lobby`), or null: the map has none. */
export const lobbyOf = (map: Pick<MapDef, 'arenas'>): Arena | null => map.arenas.find((a) => a.lobby) ?? null;

/** Is world y on a battle royale's lobby island (ADR-0190 amended), not the map proper nor another arena? */
export const onLobby = (map: Pick<MapDef, 'arenas'>, y: number): boolean => arenaAt(map, y)?.lobby === true;

/** The map's underground (ADR-0196), or null. */
export const underOf = (map: Pick<MapDef, 'arenas'>): Arena | null => map.arenas.find((a) => a.under) ?? null;

/** Is world y underground (ADR-0196)? */
export const underground = (map: Pick<MapDef, 'arenas'>, y: number): boolean => arenaAt(map, y)?.under === true;

/** The stairs a gardener is taken by (ADR-0196): stairs `i` of the map, `down` from up top. */
export interface StairsEnd {
  i: number;
  down: boolean;
}

/**
 * The stairs a gardener at (x, y) has walked far enough down or up to be taken by (ADR-0196 amended, `ROYALE.stairs`):
 * `down` px into a stairwell up top from where one comes out of it, `up` px up the steps below; or null.
 */
export function stairsAt(map: Pick<MapDef, 'stairs'>, x: number, y: number): StairsEnd | null {
  const { width, down, up, deep } = ROYALE.stairs;
  for (let i = 0; i < map.stairs.length; i++) {
    const s = map.stairs[i] as MapDef['stairs'][number];
    if (Math.abs(x - s.x) * 2 <= width && y >= s.y + down && y <= s.y + down + deep) return { i, down: true };
    if (Math.abs(x - s.ux) * 2 <= width && y <= s.uy - up && y >= s.uy - up - deep) return { i, down: false };
  }
  return null;
}

/**
 * A battle royale's crypt (ADR-0202, `MapDef.crypts`): its ways in and out, its room, its loot (content PickupKind; an
 * explosive's spot holds the round's draw instead, ADR-0202 amended: sim `cryptStock`).
 */
export interface Crypt {
  door: SpawnPoint;
  in: SpawnPoint;
  exit: SpawnPoint;
  out: SpawnPoint;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  loot: readonly { kind: number; x: number; y: number }[];
}

/** The crypt whose room world point (x, y) is in (ADR-0202), if any. */
export const cryptAt = (map: Pick<MapDef, 'crypts'>, x: number, y: number): Crypt | undefined =>
  map.crypts.find((c) => x >= c.x0 && x <= c.x1 && y >= c.y0 && y <= c.y1);

/** Is world point (x, y) in a crypt's room (ADR-0202)? */
export const inCrypt = (map: Pick<MapDef, 'crypts'>, x: number, y: number): boolean => cryptAt(map, x, y) !== undefined;

/**
 * The crypt's doorway a gardener at (x, y) has walked into (ADR-0202, `ROYALE.crypt`), and whose: `in`, its door from the
 * hall (within `deep` px of its back wall), `out`, its way out from its room (within `deep` px of its south wall); or null.
 */
export function cryptDoorAt(
  map: Pick<MapDef, 'crypts'>,
  x: number,
  y: number,
): { crypt: Crypt; door: 'in' | 'out' } | null {
  const { width, deep } = ROYALE.crypt;
  for (const c of map.crypts) {
    if (Math.abs(x - c.door.x) * 2 <= width && y >= c.door.y - 2 && y <= c.door.y + deep)
      return { crypt: c, door: 'in' };
    if (Math.abs(x - c.exit.x) * 2 <= width && y <= c.exit.y + 2 && y >= c.exit.y - deep)
      return { crypt: c, door: 'out' };
  }
  return null;
}

/** Where to walk to be taken through the crypt's door (ADR-0202): `in` deep into its doorway from the hall, `out` its way out. */
export const cryptGoal = (c: Crypt, into: boolean): { x: number; y: number } =>
  into ? { x: c.door.x, y: c.door.y + 6 } : { x: c.exit.x, y: c.exit.y - 6 };

/** Where to walk to be taken by stairs `s` (ADR-0196 amended): well down its stairwell up top (`down`), up its steps below. */
export const stairsGoal = (s: MapDef['stairs'][number], down: boolean): { x: number; y: number } =>
  down ? { x: s.x, y: s.y + ROYALE.stairs.down + 6 } : { x: s.ux, y: s.uy - ROYALE.stairs.up - 6 };

/**
 * The map proper's point over world point (x, y) (ADR-0196): itself, or the one right over it from the underground (its
 * rows the map proper's, `Arena.under`) — what the Frost bites by and where a shot below is heard from above.
 */
export function surfaceOf(map: Pick<MapDef, 'arenas'>, x: number, y: number): { x: number; y: number } {
  const a = arenaAt(map, y);
  return a?.under ? { x, y: y - a.top * TILE_SIZE } : { x, y };
}

/**
 * The stage world point (x, y) is on, in cells: its arena's (ADR-0164) or the map proper. A camera stays on one, and so
 * does everything that asks where the map ends.
 */
export const stageAt = (map: Pick<MapDef, 'width' | 'height' | 'arenas'>, y: number): Block =>
  arenaAt(map, y)?.stage ?? { cx: 0, cy: 0, w: map.width, h: yardRows(map) };

/**
 * The map proper as a map of its own (ADR-0164): `map` without its arenas' rows, sharing its cells (a tunnel burnt in
 * one shows in the other). What draws the whole map small (the minimap) draws this.
 */
export function yardOf(map: MapDef): MapDef {
  if (map.arenas.length === 0) return map;
  const height = yardRows(map);
  const n = map.width * height;
  return {
    ...map,
    height,
    ground: map.ground.subarray(0, n),
    flags: map.flags.subarray(0, n),
    interior: map.interior.subarray(0, n),
    furniture: map.furniture.subarray(0, n),
    fog: map.fog.subarray(0, n),
    props: map.props.filter((p) => p.cy < height),
    crates: map.crates.filter((c) => c.y < height * TILE_SIZE),
    arenas: [],
  };
}

/** What a house's rim may be (ADR-0120): walls, windows, doorways, or floor its things wall in (ADR-0188). */
const RIM_CHARS = new Set(['W', '+', 'D', '*']);

/**
 * An arena's stage (ADR-0164) as a map of its own: its cells copied (nothing is built or burnt there), none of the map
 * proper's places. What draws a stage small (the minimap) draws this, its world px less the stage's corner.
 */
export function stageOf(map: MapDef, arena: Arena): MapDef {
  const { cx, cy, w, h } = arena.stage;
  const cut = (a: Uint8Array) => {
    const out = new Uint8Array(w * h);
    for (let r = 0; r < h; r++) out.set(a.subarray((cy + r) * map.width + cx, (cy + r) * map.width + cx + w), r * w);
    return out;
  };
  return {
    ...map,
    width: w,
    height: h,
    ground: cut(map.ground),
    flags: cut(map.flags),
    interior: cut(map.interior),
    furniture: cut(map.furniture),
    fog: cut(map.fog),
    props: [],
    playerSpawns: [],
    teamSpawns: {},
    bases: [],
    bossDens: [],
    scarecrowSpawns: [],
    crates: [],
    camps: [],
    sparring: [],
    houses: [],
    sliders: [],
    bridges: [],
    gates: [],
    gateNights: null,
    signs: [],
    chomper: null,
    dawnScale: 1,
    ruins: [],
    crops: [],
    graves: [],
    slots: [],
    seedbeds: [],
    boards: [],
    roofs: [],
    doors: [],
    arenas: [],
    rods: [],
    fishing: [],
  };
}

/**
 * The houses of a map's rows (ADR-0120): each 4-connected group of walls, windows, floor and doorways is one, numbered in
 * reading order, its id on every cell of its rectangle (what stands on its floor too). A house must be a closed
 * rectangle: walls, windows and doorways all round, nothing of the outside within it. A house whose rim is floor, its
 * walls things (ADR-0188), may have wings: any shape of cells whose every cell by the outside is its rim; its id is on
 * its own cells, and its `roofs` box is round them all.
 */
function housesOf(id: string, width: number, height: number, charAt: (cx: number, cy: number) => string) {
  const interior = new Uint8Array(width * height);
  const roofs: Block[] = [];
  const seen = new Uint8Array(width * height);
  for (let cy = 0; cy < height; cy++)
    for (let cx = 0; cx < width; cx++) {
      if (seen[cy * width + cx] || !HOUSE_CHARS.has(charAt(cx, cy))) continue;
      let [x0, y0, x1, y1] = [cx, cy, cx, cy];
      const queue = [cy * width + cx];
      seen[cy * width + cx] = 1;
      for (let k = 0; k < queue.length; k++) {
        const i = queue[k] as number;
        const x = i % width;
        const y = (i - x) / width;
        [x0, y0, x1, y1] = [Math.min(x0, x), Math.min(y0, y), Math.max(x1, x), Math.max(y1, y)];
        for (const [nx, ny] of [
          [x - 1, y],
          [x + 1, y],
          [x, y - 1],
          [x, y + 1],
        ] as const) {
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          const j = ny * width + nx;
          if (!seen[j] && HOUSE_CHARS.has(charAt(nx, ny))) {
            seen[j] = 1;
            queue.push(j);
          }
        }
      }
      const house = roofs.length + 1;
      if (house > 255) throw new Error(`Map ${id}: more than 255 houses`);
      // Walls of things (a rim of floor): its own cells and what stands on them, whatever its shape.
      if (charAt(cx, cy) === '*') {
        const own = new Set(queue);
        const round = (x: number, y: number) => [
          [x - 1, y],
          [x + 1, y],
          [x, y - 1],
          [x, y + 1],
        ];
        // An object standing on its floor (a chest) is its, if its cells are all round it.
        const mine = (x: number, y: number) =>
          own.has(y * width + x) ||
          (GROUND_CHARS[charAt(x, y)] === undefined &&
            round(x, y).every(([nx, ny]) => own.has((ny as number) * width + (nx as number))));
        for (let y = y0; y <= y1; y++)
          for (let x = x0; x <= x1; x++) {
            if (!mine(x, y)) continue;
            const ch = charAt(x, y);
            if (round(x, y).some(([nx, ny]) => !mine(nx as number, ny as number)) && !RIM_CHARS.has(ch))
              throw new Error(`Map ${id}: house ${house} at ${x0},${y0} is open to the outside ('${ch}' at ${x},${y})`);
            if (interior[y * width + x]) throw new Error(`Map ${id}: houses overlap at ${x},${y}`);
            interior[y * width + x] = house;
          }
        roofs.push({ cx: x0, cy: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 });
        continue;
      }
      for (let y = y0; y <= y1; y++)
        for (let x = x0; x <= x1; x++) {
          const ch = charAt(x, y);
          const rim = x === x0 || x === x1 || y === y0 || y === y1;
          if (rim ? !RIM_CHARS.has(ch) : GROUND_CHARS[ch] !== undefined && !HOUSE_CHARS.has(ch))
            throw new Error(`Map ${id}: house ${house} at ${x0},${y0} is no closed rectangle ('${ch}' at ${x},${y})`);
          if (interior[y * width + x]) throw new Error(`Map ${id}: houses overlap at ${x},${y}`);
          interior[y * width + x] = house;
        }
      roofs.push({ cx: x0, cy: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 });
    }
  return { interior, roofs };
}

/**
 * Where grandpa rocks in front of cottage `b` (ADR-0100), on the porch's edge: a dummy anyone may shoot (ADR-0101), a
 * scarecrow drawn as him.
 */
export const porchSeat = (b: Block): SpawnPoint => ({
  x: (b.cx + b.w / 2) * TILE_SIZE - 22,
  y: (b.cy + b.h) * TILE_SIZE + 4,
});

/** Where a player of `team` may spawn: the team's spawns on war maps, neutral spawns otherwise. */
export function spawnsFor(map: MapDef, team: number): SpawnPoint[] {
  const own = map.teamSpawns[team];
  return own && own.length > 0 ? own : map.playerSpawns;
}

/** Flags of a cell; everything outside the map is a solid, shot-blocking wall. */
export function cellFlags(map: MapDef, cx: number, cy: number): number {
  if (cx < 0 || cy < 0 || cx >= map.width || cy >= map.height) return CellFlag.Solid | CellFlag.BlocksShots;
  return map.flags[cy * map.width + cx] as number;
}

export function groundAt(map: MapDef, cx: number, cy: number): Ground {
  if (cx < 0 || cy < 0 || cx >= map.width || cy >= map.height) return Ground.Hedge;
  return map.ground[cy * map.width + cx] as Ground;
}

/** The house (ADR-0120) cell (cx, cy) belongs to — its walls, windows and doorways too — or 0 outside one. */
export const interiorAt = (map: MapDef, cx: number, cy: number): number =>
  cx < 0 || cy < 0 || cx >= map.width || cy >= map.height ? 0 : (map.interior[cy * map.width + cx] as number);

/** The house world point (x, y) is in, or 0. */
export const houseAt = (map: MapDef, x: number, y: number): number =>
  interiorAt(map, Math.floor(x / TILE_SIZE), Math.floor(y / TILE_SIZE));

export const isSolid = (map: MapDef, cx: number, cy: number): boolean =>
  (cellFlags(map, cx, cy) & CellFlag.Solid) !== 0;

export const blocksShots = (map: MapDef, cx: number, cy: number): boolean =>
  (cellFlags(map, cx, cy) & CellFlag.BlocksShots) !== 0;

/** Is cell (cx, cy) a pit of lava (ADR-0187)? */
export const isLava = (map: MapDef, cx: number, cy: number): boolean => (cellFlags(map, cx, cy) & CellFlag.Lava) !== 0;

/** Sight is blocked by whatever blocks shots (hedges, thickets, trees, rocks) — water is not, nor are crates (entities). */
export const blocksVision = blocksShots;

/** What cell (cx, cy) stops (`Stop` bits): feet if it is solid, shots and sight if it blocks shots. */
export const cellStops = (map: MapDef, cx: number, cy: number): number => {
  const f = cellFlags(map, cx, cy);
  return (f & CellFlag.Solid ? Stop.Feet : 0) | (f & CellFlag.BlocksShots ? Stop.Shots | Stop.Sight : 0);
};

/**
 * Calls `fn` with the index of each shape on cell (cx, cy) that stops any of `stops` (ADR-0188), a broken thing's never;
 * a shape reaching into several cells is on each of them.
 */
export function forShapes(map: MapDef, cx: number, cy: number, stops: number, fn: (s: number) => void): void {
  if (map.shapes.length === 0 || cx < 0 || cy < 0 || cx >= map.width || cy >= map.height) return;
  const { start, list } = map.shapeIndex;
  const i = cy * map.width + cx;
  for (let k = start[i] as number; k < (start[i + 1] as number); k++) {
    const s = list[k] as number;
    const shape = map.shapes[s] as Shape;
    if (shape.stops & stops && !map.broken[shape.thing]) fn(s);
  }
}

/** Where a segment met the map (`raycastMap`): a cell's side, or a thing's shape (`thing` ≥ 0). */
export interface MapHit {
  /** Fraction along the segment, 0 … 1. */
  t: number;
  x: number;
  y: number;
  cellX: number;
  cellY: number;
  /** The normal of the side it met (0, 0 when it starts inside). */
  nx: number;
  ny: number;
  /** The thing whose shape it met, -1 for a cell. */
  thing: number;
}

const hitNormal: [number, number] = [0, 0];

/**
 * Does the segment from (x0, y0) to (x1, y1) meet anything that stops `stops` — a cell (by its flags: `cellStops`) or a
 * thing's shape (ADR-0188)? `out` gets the first such meeting. A segment starting in a stopping cell or shape meets it
 * at 0. Shots fly (`Stop.Shots`), sight sees (`Stop.Sight`) and blasts reach over it alike on the server and the client.
 */
export function raycastMap(
  map: MapDef,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  stops: number,
  out: MapHit,
): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  let cx = Math.floor(x0 / TILE_SIZE);
  let cy = Math.floor(y0 / TILE_SIZE);
  out.t = 2;
  out.thing = -1;
  const shapes = map.shapes.length > 0;
  /** The shapes on cell (cx, cy) the segment meets before the best so far. */
  const meet = (cx: number, cy: number) => {
    forShapes(map, cx, cy, stops, (s) => {
      const t = segmentShape(x0, y0, dx, dy, map.shapes[s] as Shape, hitNormal);
      if (t < 0 || t >= out.t) return;
      out.t = t;
      out.nx = hitNormal[0];
      out.ny = hitNormal[1];
      out.cellX = cx;
      out.cellY = cy;
      out.thing = (map.shapes[s] as Shape).thing;
    });
  };
  const done = () => {
    if (out.t > 1) return false;
    out.x = x0 + dx * out.t;
    out.y = y0 + dy * out.t;
    return true;
  };
  if (cellStops(map, cx, cy) & stops) {
    out.t = 0;
    out.nx = 0;
    out.ny = 0;
    out.cellX = cx;
    out.cellY = cy;
    return done();
  }
  if (shapes) meet(cx, cy);
  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  const tDeltaX = stepX !== 0 ? Math.abs(TILE_SIZE / dx) : Number.POSITIVE_INFINITY;
  const tDeltaY = stepY !== 0 ? Math.abs(TILE_SIZE / dy) : Number.POSITIVE_INFINITY;
  let tMaxX =
    stepX > 0 ? ((cx + 1) * TILE_SIZE - x0) / dx : stepX < 0 ? (cx * TILE_SIZE - x0) / dx : Number.POSITIVE_INFINITY;
  let tMaxY =
    stepY > 0 ? ((cy + 1) * TILE_SIZE - y0) / dy : stepY < 0 ? (cy * TILE_SIZE - y0) / dy : Number.POSITIVE_INFINITY;
  const steps = Math.abs(Math.floor(x1 / TILE_SIZE) - cx) + Math.abs(Math.floor(y1 / TILE_SIZE) - cy) + 2;
  for (let i = 0; i < steps; i++) {
    let t: number;
    let nx = 0;
    let ny = 0;
    if (tMaxX < tMaxY) {
      t = tMaxX;
      cx += stepX;
      tMaxX += tDeltaX;
      nx = -stepX;
    } else {
      t = tMaxY;
      cy += stepY;
      tMaxY += tDeltaY;
      ny = -stepY;
    }
    // Nothing further on can be met sooner than what was.
    if (t > 1 || t >= out.t) break;
    if (cellStops(map, cx, cy) & stops) {
      out.t = t;
      out.nx = nx;
      out.ny = ny;
      out.cellX = cx;
      out.cellY = cy;
      out.thing = -1;
      break;
    }
    if (shapes) meet(cx, cy);
  }
  return done();
}

/**
 * The thing (ADR-0188) whose shape stopping `stops` comes nearest (x, y), within `r` of its edge (inside it counts as 0),
 * -1 if none does: what a spawn or a drop would stand in, what a shot met.
 */
export function thingNear(map: MapDef, x: number, y: number, r: number, stops: number): number {
  if (map.shapes.length === 0) return -1;
  let best = -1;
  let bestD = r;
  for (let cy = Math.floor((y - r) / TILE_SIZE); cy <= Math.floor((y + r) / TILE_SIZE); cy++)
    for (let cx = Math.floor((x - r) / TILE_SIZE); cx <= Math.floor((x + r) / TILE_SIZE); cx++)
      forShapes(map, cx, cy, stops, (s) => {
        const sh = map.shapes[s] as Shape;
        const d =
          sh.r > 0
            ? Math.max(0, hypot(x - sh.x, y - sh.y) - sh.r)
            : hypot(
                x - Math.max(sh.x - sh.hw, Math.min(x, sh.x + sh.hw)),
                y - Math.max(sh.y - sh.hh, Math.min(y, sh.y + sh.hh)),
              );
        if (d < bestD || (d === 0 && best < 0)) {
          bestD = d;
          best = sh.thing;
        }
      });
  return best;
}

/**
 * Does a body of radius `r` stand at (x, y) as it is, with nothing to push it out: no wall, water or lava under it (the
 * cells its box covers; off the map is wall), no thing that stops feet (ADR-0188), no Greenhouse and no scarecrow within
 * `r`. PLAY's party walks on it and the match seats a gardener where it stood there (ADR-0235 amended).
 */
export function footing(map: MapDef, x: number, y: number, r: number): boolean {
  for (const dx of [-r, r])
    for (const dy of [-r, r])
      if (isSolid(map, Math.floor((x + dx) / TILE_SIZE), Math.floor((y + dy) / TILE_SIZE))) return false;
  const clear = (spots: readonly { x: number; y: number }[], body: number) =>
    spots.every((s) => hypot(s.x - x, s.y - y) >= body + r);
  return (
    thingNear(map, x, y, r, Stop.Feet) < 0 &&
    clear(map.bases, BASE.bodyRadius) &&
    clear(map.scarecrowSpawns, SCARECROW.bodyRadius)
  );
}

const swept: MapHit = { t: 0, x: 0, y: 0, cellX: 0, cellY: 0, nx: 0, ny: 0, thing: -1 };
/**
 * Does a body (`r` either side of its middle) step from cell (cx, cy) to the cell above or below it, row `ny`, past the
 * map's things (ADR-0188)? Planners walk cell middles, but a doorway's middle need not be a cell's (a cell's middle may be
 * a hair past a jamb the body slides by), so the body is swept across the cell at a few places: open if it clears at one.
 * Never sideways: a doorway's cells are a house's, its walls on either hand.
 */
export function throughDoorway(map: MapDef, cx: number, cy: number, ny: number, r = 4.5): boolean {
  const [y0, y1] = [(cy + 0.5) * TILE_SIZE, (ny + 0.5) * TILE_SIZE];
  for (const off of [0, -3, 3, -6, 6]) {
    const x = (cx + 0.5) * TILE_SIZE + off;
    if ([-r, 0, r].every((e) => !raycastMap(map, x + e, y0, x + e, y1, Stop.Feet, swept) || swept.thing < 0))
      return true;
  }
  return false;
}

/** Thing `i` broke (ADR-0188): its shapes stop nothing on this copy of the map from now on. */
export function breakThing(map: MapDef, i: number): void {
  if (i < 0 || i >= map.broken.length || map.broken[i]) return;
  map.broken[i] = 1;
  // What stops sight changed: sight kept traced for it is stale (ADR-0044 amended, as for a carved cell).
  map.broke = (map.broke ?? 0) + 1;
}

/**
 * Can a Railgun burn through this cell (ADR-0038)? Hedges and thickets only — never trees, rocks, crates, altars or
 * water — and never the map's outer ring, which keeps everyone on the map.
 */
export function isCarvable(map: MapDef, cx: number, cy: number): boolean {
  // The map proper's last row keeps everyone out of an arena under it (ADR-0164), and nothing in one burns.
  if (cx < 1 || cy < 1 || cx >= map.width - 1 || cy >= yardRows(map) - 1) return false;
  const i = cy * map.width + cx;
  const g = map.ground[i];
  return (g === Ground.Hedge || g === Ground.Thicket) && ((map.flags[i] as number) & CellFlag.BlocksShots) !== 0;
}

/**
 * A copy of `map` whose cells can be changed (`scorchCells`) without touching the original: `getMap` hands every
 * caller the same object, so a world, a client or a bot that carves its map works on its own copy.
 */
export const cloneMap = (map: MapDef): MapDef => ({
  ...map,
  ground: map.ground.slice(),
  flags: map.flags.slice(),
  broken: map.broken.slice(),
});

/** Burns cells (indices `cy * width + cx`) to open, scorched ground (ADR-0038). Idempotent. */
export function scorchCells(map: MapDef, cells: readonly number[]): void {
  for (const i of cells) {
    if (i < 0 || i >= map.ground.length) continue;
    map.ground[i] = Ground.Scorched;
    map.flags[i] = 0;
  }
  map.carved = (map.carved ?? 0) + cells.length;
}

/**
 * Turns `cells` (indices `cy * width + cx`: hedges and ponds) into pits of lava, or back to what their ground is (`on`
 * false): Zombie Nights' hell (ADR-0187), on a world's or a client's own copy (`cloneMap`). A pit is no wall: shots and
 * sight go over it (what caches what walls hide checks `carved`, as for a burn).
 */
export function setLava(map: MapDef, cells: readonly number[], on: boolean): void {
  for (const i of cells) map.flags[i] = on ? CellFlag.Solid | CellFlag.Lava : flagsFor(map.ground[i] as Ground);
  if (cells.length > 0) map.carved = (map.carved ?? 0) + cells.length;
}

/** True when the cell under world point (x, y) is jungle under fog of war. */
export function isFogged(map: MapDef, x: number, y: number): boolean {
  const cx = Math.floor(x / TILE_SIZE);
  const cy = Math.floor(y / TILE_SIZE);
  if (cx < 0 || cy < 0 || cx >= map.width || cy >= map.height) return false;
  return map.fog[cy * map.width + cx] === 1;
}
